import { Group, SegmentedControl, Text } from '@mantine/core';
import {
  IconListCheckFilled,
  IconDeviceMobileMessage,
  IconList,
  IconMessage,
  IconNote,
  IconPhone,
} from '@tabler/icons-react';
import { TypeFilter } from './types';
import { ReactElement } from 'react';
import { InboundIcon } from '../icons/InboundIcon';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@bariendo/lib/constants';

interface CommunicationTypeFilterProps {
  value: TypeFilter;
  onChange: (value: TypeFilter) => void;
}

// No count badge here: the left-nav Inbox badge is the single count, and it
// includes open clinical tasks (see useNotificationsCount).
function TasksFilterLabel(): ReactElement {
  return (
    <Group gap="xs" wrap="nowrap">
      <IconListCheckFilled size={16} />
      <Text>Tasks</Text>
    </Group>
  );
}

export function CommunicationTypeFilter({ value, onChange }: CommunicationTypeFilterProps): ReactElement {
  // Gate the SEGMENT, not its label. Hiding only the label left a blank but
  // fully enabled control in the row: it still rendered, still took a click,
  // and still selected the Tasks view. The flag reads false until PostHog
  // responds, so that blank gap appeared on every inbox load, not just where
  // task management is disabled.
  const isTasksEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_TASK_MANAGEMENT);

  return (
    <SegmentedControl
      value={value}
      onChange={(value) => onChange(value as TypeFilter)}
      withItemsBorders={false}
      data={[
        {
          label: (
            <Group gap="xs" wrap="nowrap">
              <IconList size={16} />
              <Text>All items</Text>
            </Group>
          ),
          value: TypeFilter.ALL,
        },
        ...(isTasksEnabled
          ? [
              {
                label: <TasksFilterLabel />,
                value: TypeFilter.TASKS,
              },
            ]
          : []),
        {
          label: (
            <Group gap="xs" wrap="nowrap">
              <IconNote size={16} />
              <Text>Notes</Text>
            </Group>
          ),
          value: TypeFilter.NOTE,
        },
        {
          label: (
            <Group gap="xs" wrap="nowrap">
              <IconMessage size={16} />
              <Text>Chat</Text>
            </Group>
          ),
          value: TypeFilter.CHAT,
        },
        {
          label: (
            <Group gap="xs" wrap="nowrap">
              <IconDeviceMobileMessage size={16} />
              <Text>SMS</Text>
            </Group>
          ),
          value: TypeFilter.SMS,
        },
        {
          label: (
            <Group gap="xs" wrap="nowrap">
              <IconPhone size={16} />
              <Text>Calls</Text>
            </Group>
          ),
          value: TypeFilter.CALL,
        },
        {
          label: (
            <Group gap="xs" wrap="nowrap">
              <InboundIcon size={16} theme="dark" filter="invert(0.3)" />
              <Text>Fax</Text>
            </Group>
          ),
          value: TypeFilter.FAX,
        },
      ]}
    />
  );
}
